import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllStaffHistories,
  getStaffHistoryById,
  getStaffHistoryByStaffId,
  updateStaffHistory,
  deleteStaffHistory
} from '../services/staffHistoryService';
import Notification from '../components/Common/Notification';

export const StaffHistoryContext = createContext();

export const StaffHistoryProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [staffHistories, setStaffHistories] = useState([]);
  const [selectedStaffHistory, setSelectedStaffHistory] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllStaffHistories = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllStaffHistories(authHeaders);
      setStaffHistories(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching staff histories.';
      showNotification(errorMessage, true);
    }
  };

  const fetchStaffHistoryById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStaffHistoryById(id, authHeaders);
      setSelectedStaffHistory(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching staff history with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchStaffHistoryByStaffId = async (staffId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStaffHistoryByStaffId(staffId, authHeaders);
      setStaffHistories(data); // Update staffHistories with the fetched data
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching staff history for staff ID ${staffId}.`;
      showNotification(errorMessage, true);
    }
  };

  const updateExistingStaffHistory = async (id, staffHistoryData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedStaffHistory = await updateStaffHistory(id, staffHistoryData, authHeaders);

      setStaffHistories(prevHistories => {
        if (!Array.isArray(prevHistories)) {
          console.error('Expected prevHistories to be an array but received:', prevHistories);
          return [updatedStaffHistory];
        }

        return prevHistories.map(history => (history.id === id ? updatedStaffHistory : history));
      });

      showNotification('Staff history updated successfully!', false);
      return updatedStaffHistory;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating staff history.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeStaffHistory = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteStaffHistory(id, authHeaders);
      setStaffHistories(prevHistories => prevHistories.filter(history => history.id !== id));
      showNotification('Staff history removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing staff history.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllStaffHistories();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <StaffHistoryContext.Provider
      value={{
        staffHistories,
        selectedStaffHistory,
        fetchAllStaffHistories,
        fetchStaffHistoryById,
        fetchStaffHistoryByStaffId,
        updateExistingStaffHistory,
        removeStaffHistory,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </StaffHistoryContext.Provider>
  );
};
